import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Avatar, Badge } from '@components';
import { PerformanceMetrics, AgentPerformance, CallVolumeTrend, RecentCall } from '../types';
import { getTrendIcon, getTrendColor, getStatusBadge, formatTrend } from '../utils/analyticsUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface AnalyticsTabsProps {
  performanceMetrics: PerformanceMetrics | null;
  agentPerformance: AgentPerformance[];
  callVolumeTrends: CallVolumeTrend[];
  recentCalls: RecentCall[];
  onViewAgentDetails: (agentId: string) => void;
  onUpdateUsageChart: (metric: string) => void;
}

export const AnalyticsTabs: React.FC<AnalyticsTabsProps> = ({
  performanceMetrics,
  agentPerformance,
  callVolumeTrends,
  recentCalls,
  onViewAgentDetails,
  onUpdateUsageChart: _onUpdateUsageChart,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'performance'>('overview');

  const getStatusTranslation = (status: string) => {
    switch (status) {
      case 'initiated':
        return t('calls.initiated');
      case 'ringing':
        return t('calls.ringing');
      case 'answered':
        return t('calls.answered');
      case 'in_progress':
        return t('calls.inProgress');
      case 'completed':
        return t('calls.completed');
      case 'failed':
        return t('calls.failed');
      case 'cancelled':
        return t('calls.cancelled');
      case 'busy':
        return t('calls.busy');
      case 'no_answer':
        return t('calls.noAnswer');
      case 'transferred':
        return t('calls.transferred');
      case 'escalated':
        return t('calls.escalated');
      default:
        return status;
    }
  };

  return (
    <div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('analytics.overview')}
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
              activeTab === 'performance'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('analytics.performance')}
          </button>
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="mt-6 space-y-6">
          {/* Call Volume Trends */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('analytics.callVolumeTrends')}
              </h3>
            </div>
            <Card>
              <CardContent>
                {callVolumeTrends.length > 0 ? (
                  <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={callVolumeTrends.map(trend => ({
                          date: new Date(trend.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          }),
                          count: trend.count,
                          fullDate: trend.date,
                        }))}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="currentColor"
                          className="opacity-20"
                        />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: 'currentColor' }}
                          className="text-gray-600 dark:text-gray-400"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          tick={{ fill: 'currentColor' }}
                          className="text-gray-600 dark:text-gray-400"
                          style={{ fontSize: '12px' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgb(255, 255, 255)',
                            border: '1px solid rgb(229, 231, 235)',
                            borderRadius: '8px',
                            color: 'rgb(17, 24, 39)',
                          }}
                          labelStyle={{ color: 'rgb(17, 24, 39)' }}
                          formatter={(value: number) => [value, 'Calls']}
                          cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                        />
                        <Bar dataKey="count" fill="#45D0BD" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 bg-gray-50 dark:bg-gray-800/50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <span className="material-icons text-4xl text-primary-500">bar_chart</span>
                      <p className="text-gray-600 dark:text-gray-400 mt-2">
                        {t('analytics.noDataAvailable')}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Calls */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              {t('analytics.recentCalls')}
            </h3>
            <Card>
              <CardContent>
                <div className="space-y-4">
                  {recentCalls.length === 0 ? (
                    <div className="text-center py-8">
                      <span className="material-icons text-4xl text-gray-400 dark:text-gray-500">
                        phone
                      </span>
                      <p className="text-gray-600 dark:text-gray-400 mt-2">
                        {t('analytics.noRecentCalls')}
                      </p>
                    </div>
                  ) : (
                    recentCalls.slice(0, 5).map(call => (
                      <div
                        key={call.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center">
                              <span className="material-icons text-primary-600 dark:text-primary-400">
                                phone
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {call.caller_number} → {call.called_number}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {call.agent_name} • {call.duration}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge
                            variant={call.status === 'completed' ? 'success' : 'warning'}
                            size="sm"
                          >
                            {getStatusTranslation(call.status)}
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(call.created).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="mt-6 space-y-6">
          {/* Performance Metrics */}
          {performanceMetrics && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {t('analytics.performanceMetrics')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t('analytics.totalCalls')}
                      </span>
                      <span className="material-icons text-gray-400 dark:text-gray-500">phone</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      {performanceMetrics.total_calls.value}
                    </div>
                    <div
                      className={`flex items-center text-sm ${getTrendColor(performanceMetrics.total_calls.trend_direction as 'up' | 'down')}`}
                    >
                      <span className="material-icons text-base mr-1">
                        {getTrendIcon(
                          performanceMetrics.total_calls.trend_direction as 'up' | 'down'
                        )}
                      </span>
                      {formatTrend(performanceMetrics.total_calls.trend)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t('analytics.successRate')}
                      </span>
                      <span className="material-icons text-gray-400 dark:text-gray-500">
                        check_circle
                      </span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      {performanceMetrics.success_rate.value}
                    </div>
                    <div
                      className={`flex items-center text-sm ${getTrendColor(performanceMetrics.success_rate.trend_direction as 'up' | 'down')}`}
                    >
                      <span className="material-icons text-base mr-1">
                        {getTrendIcon(
                          performanceMetrics.success_rate.trend_direction as 'up' | 'down'
                        )}
                      </span>
                      {formatTrend(performanceMetrics.success_rate.trend)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t('analytics.avgCallDuration')}
                      </span>
                      <span className="material-icons text-gray-400 dark:text-gray-500">
                        schedule
                      </span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      {performanceMetrics.avg_call_duration.value}
                    </div>
                    <div
                      className={`flex items-center text-sm ${getTrendColor(performanceMetrics.avg_call_duration.trend_direction as 'up' | 'down')}`}
                    >
                      <span className="material-icons text-base mr-1">
                        {getTrendIcon(
                          performanceMetrics.avg_call_duration.trend_direction as 'up' | 'down'
                        )}
                      </span>
                      {formatTrend(performanceMetrics.avg_call_duration.trend)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Agent Performance */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              {t('analytics.agentPerformance')}
            </h3>
            <Card>
              <CardContent>
                {agentPerformance.length === 0 ? (
                  <div className="text-center py-12">
                    <span className="material-icons text-4xl text-gray-400 dark:text-gray-500">
                      people
                    </span>
                    <p className="text-gray-600 dark:text-gray-400 mt-4 text-lg">
                      {t('analytics.noDataAvailable')}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                      {t('analytics.noAgentPerformanceData')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.agent')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.role')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.callsHandled')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.successRate')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.avgDuration')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.status')}
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                            {t('analytics.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentPerformance.map(agent => (
                          <tr
                            key={agent.agent_id}
                            className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center space-x-3">
                                <Avatar size="sm">{agent.agent_name.charAt(0)}</Avatar>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {agent.agent_name}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                              {agent.role}
                            </td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                              {agent.calls_handled}
                            </td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                              {agent.success_rate}
                            </td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                              {agent.avg_duration}
                            </td>
                            <td className="py-3 px-4">{getStatusBadge(agent.status, t)}</td>
                            <td className="py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onViewAgentDetails(agent.agent_id)}
                                className="p-1 h-8 w-8"
                                title="View Agent Details"
                              >
                                <span className="material-icons text-lg">visibility</span>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
